import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  loadTrendNotifySettings,
  registerTrendNotifyRemote,
  subscribeToTrendNotifyRow,
  TREND_NOTIFY_SYNC_EVENT,
} from "@/lib/trendNotifyRemote";
import {
  clearTrendHistory,
  dismissHistoryEntry,
  markAllTrendsRead,
  markHistoryEntryRead,
  markTrendSeen,
  readTrendNotifySettings,
  unreadHistoryCount,
  writeTrendNotifySettings,
  type TrendNotifyMode,
  type TrendNotifySettings,
} from "@/lib/trendNotifications";
import { disableTrendPush, enableTrendPush } from "@/lib/trendPush";

/**
 * Reads the trend notification settings, hydrating them from the user's profile
 * so the choice (and read/dismissed state) stays consistent across devices.
 *
 * Every mutation is optimistic: local state and localStorage update instantly
 * while the profile write happens in the background, and realtime updates from
 * other devices are merged back in as they arrive.
 */
export function useTrendNotifySettings() {
  const [settings, setSettings] = useState<TrendNotifySettings>(() =>
    readTrendNotifySettings(),
  );
  /** True while a background profile write is still in flight. */
  const [syncing, setSyncing] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);

  useEffect(() => {
    registerTrendNotifyRemote();
    let cancelled = false;
    let unsubscribeRow: (() => void) | null = null;

    loadTrendNotifySettings().then((s) => {
      if (!cancelled) setSettings(s);
    });

    supabase.auth.getUser().then(({ data }) => {
      if (cancelled || !data.user) return;
      unsubscribeRow = subscribeToTrendNotifyRow(data.user.id, (remote) => {
        if (!cancelled) setSettings(remote);
      });
    });

    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<TrendNotifySettings>).detail;
      if (detail) setSettings(detail);
    };
    const onSync = (e: Event) => {
      const detail = (e as CustomEvent<{ pending: boolean; failed: boolean }>).detail;
      setSyncing(!!detail?.pending);
      if (detail?.failed) setSyncFailed(true);
      else if (detail && !detail.pending) setSyncFailed((prev) => prev && false);
    };
    window.addEventListener("td:trend-notify-settings", onChange as EventListener);
    window.addEventListener(TREND_NOTIFY_SYNC_EVENT, onSync as EventListener);
    return () => {
      cancelled = true;
      unsubscribeRow?.();
      window.removeEventListener("td:trend-notify-settings", onChange as EventListener);
      window.removeEventListener(TREND_NOTIFY_SYNC_EVENT, onSync as EventListener);
    };
  }, []);

  const update = useCallback((next: TrendNotifySettings) => {
    setSettings(next); // optimistic: UI reflects the change immediately
    writeTrendNotifySettings(next);
  }, []);

  const setMode = useCallback(
    (mode: TrendNotifyMode) => update({ ...readTrendNotifySettings(), mode }),
    [update],
  );
  const setEmailAlerts = useCallback(
    (email: boolean) => update({ ...readTrendNotifySettings(), email }),
    [update],
  );
  /** Toggling push also handles the browser permission + subscription. */
  const setPushAlerts = useCallback(
    async (push: boolean) => {
      if (push) {
        const granted = await enableTrendPush();
        update({ ...readTrendNotifySettings(), push: granted });
        return granted;
      }
      await disableTrendPush();
      update({ ...readTrendNotifySettings(), push: false });
      return false;
    },
    [update],
  );
  const markAllRead = useCallback(
    () => update(markAllTrendsRead(readTrendNotifySettings())),
    [update],
  );
  const markSeen = useCallback(
    (trendId: string) => update(markTrendSeen(readTrendNotifySettings(), trendId)),
    [update],
  );
  const markHistoryRead = useCallback(
    (entryId: string) => update(markHistoryEntryRead(readTrendNotifySettings(), entryId)),
    [update],
  );
  const dismissHistory = useCallback(
    (entryId: string) => update(dismissHistoryEntry(readTrendNotifySettings(), entryId)),
    [update],
  );
  const clearHistory = useCallback(
    () => update(clearTrendHistory(readTrendNotifySettings())),
    [update],
  );

  return {
    settings,
    unreadCount: settings.unreadIds.length,
    historyUnreadCount: unreadHistoryCount(settings),
    syncing,
    syncFailed,
    setMode,
    setEmailAlerts,
    setPushAlerts,
    markAllRead,
    markSeen,
    markHistoryRead,
    dismissHistory,
    clearHistory,
  };
}
